import { FC } from 'react';

import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useForm, Controller } from 'react-hook-form';

import { IconButton, DialogTitle, DialogContent, Select, MenuItem, InputLabel, TextField, Box } from '@mui/material';
import Button from '@mui/lab/LoadingButton';
import CloseIcon from '@mui/icons-material/Close';
import { getMarks } from 'api/marks';
import { getTournamentFormatsList } from 'api/tournamentformats';
import { creatPrizeDistributionGroupRate } from 'api/prizedistributiongroups';

type Props = {
    /**
     * Функция закрытия диалогового окна.
     */
    close: () => void;

    /**
     * ID маркера
     */
    id: string;
}

/**
 * Диалоговое окно для создания правила распределения призов.
 */
const PrizeDistributionNewItemDialog: FC<Props> = ({ close, id }) => {
    const queryClient = useQueryClient();

    const { data: marks, isFetching: markFetching } = useQuery(['tournaments-marks-data'], getMarks, {
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        retry: false
    })

    const {data: formats, isFetching} = useQuery(['tournament-formats'], getTournamentFormatsList, {
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        retry: false
    },)

    const { mutate } = useMutation(creatPrizeDistributionGroupRate, {
        onSuccess: () => {
            queryClient.invalidateQueries('group-list-prize-distribution');
            close();
        },
    });

    const { control, handleSubmit } = useForm();

    const onSubmit = (data) => {

        const body = {
            label_id: data.mark,
            tournament_format_id: data.formats,
            ratio: parseFloat(data.rate),
        }

        if (!body.label_id || !body.ratio || !body.tournament_format_id) {
            return;
        }

        mutate({ id, ...body })
    };

    return (
        <>
            <DialogTitle>
                Добавление доли
                <IconButton
                    aria-label='close'
                    onClick={() => {
                        close();
                    }}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            {!markFetching && !isFetching && (
                <DialogContent>
                    <form className='flex flex-col gap-2'>
                        <Controller
                            control={control}
                            name={`mark`}
                            defaultValue=''
                            render={({ field }) => (
                                <Box className='flex flex-col gap-2'>
                                    <InputLabel id="select-label">Метка</InputLabel>
                                    <Select labelId="select-label" {...field} size="small" className='mb-4'>
                                        {marks.map((item) => (
                                            <MenuItem key={item.id} value={item.id}>
                                                {item.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            )}
                        />

                        <Controller
                            control={control}
                            name={`formats`}
                            defaultValue=''
                            render={({ field }) => (
                                <Box className='flex flex-col gap-2'>
                                    <InputLabel id="select-label">Формат турнира</InputLabel>
                                    <Select labelId="select-label" {...field} size="small" className='mb-4'>
                                        {formats.map((item) => (
                                            <MenuItem key={item.id} value={item.id}>
                                                {item.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Box>
                            )}
                        />
                       
                        <Controller
                            name={`rate`}
                            control={control}
                            defaultValue=""
                            render={({ field }) => <TextField {...field} label="Доля" type="number" size="small" />}
                        />
                        
                        <Box>
                            <div className='pt-4'>
                                <Button
                                    fullWidth
                                    variant='contained'
                                    type='button'
                                    size="small"
                                    onClick={handleSubmit(onSubmit)}
                                >
                                    Сохранить
                                </Button>
                            </div>
                        </Box>
                    </form>
                </DialogContent>
            )}
        </>
    );
};

export default PrizeDistributionNewItemDialog;
